import { useCallback, useState } from 'react';

import {
  classifyMediaError,
  detectBrowser,
  isSecureContext,
  recoverySteps,
  type PermissionProblem,
} from './permissions';

/**
 * Asks for camera and microphone, and explains the fix when that fails.
 *
 * The request fires from a real button press, never automatically on mount:
 * browsers only surface the permission dialog from a user gesture, and an
 * auto-request that silently no-ops leaves the patient staring at a blank page.
 */
export function PermissionGate({
  onGranted,
  onShowLicences,
}: {
  onGranted: () => void;
  /**
   * Opens the third-party licences (LICENSE-CLEARANCE rows 4/9/10) from the
   * footer line — the join page is the one screen every patient sees, so it
   * is where the notices the bundle owes are reachable from.
   */
  onShowLicences?: () => void;
}) {
  const [requesting, setRequesting] = useState(false);
  const [problem, setProblem] = useState<PermissionProblem | null>(null);

  const request = useCallback(async () => {
    setRequesting(true);
    setProblem(null);
    try {
      if (!isSecureContext(window)) {
        setProblem('insecure-context');
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setProblem('unsupported');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      // Release immediately: agora-rtc-react acquires its own tracks, and
      // holding these would leave the camera busy for the SDK.
      for (const track of stream.getTracks()) track.stop();
      onGranted();
    } catch (err) {
      setProblem(classifyMediaError(err as { name?: string }));
    } finally {
      setRequesting(false);
    }
  }, [onGranted]);

  if (problem) {
    const recovery = recoverySteps(problem, detectBrowser(navigator.userAgent));
    return (
      <div className="screen">
        <h1>{recovery.title}</h1>
        <ol>
          {recovery.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <button onClick={() => void request()} disabled={requesting}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="screen">
      <h1>Ready to join</h1>
      <p>
        Your physiotherapist needs to see and hear you. Tap below, then choose{' '}
        <strong>Allow</strong> when your phone asks.
      </p>
      <button onClick={() => void request()} disabled={requesting}>
        {requesting ? 'Waiting…' : 'Allow camera and microphone'}
      </button>
      {onShowLicences ? (
        <p className="footnote">
          Built with open-source software ·{' '}
          <button className="link" onClick={onShowLicences}>
            Third-party licences
          </button>
        </p>
      ) : null}
    </div>
  );
}
